import { useState } from "react";
import axios from "../../api/axios";
import { toast } from "react-toastify";

export default function DiscountCoupon({
  cartItems = [],
  cartSubtotal = 0, // Renamed from cartTotal to be clearer
  onDiscountApplied,
  onDiscountRemoved,
  appliedDiscount = null,
  disabled = false,
  className = "",
}) {
  const [couponCode, setCouponCode] = useState("");
  const [isValidating, setIsValidating] = useState(false);
  const [isApplying, setIsApplying] = useState(false);

  // Validate coupon code
  const validateCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error("Please enter a coupon code");
      return;
    }

    if (cartItems.length === 0) {
      toast.error("Your cart is empty");
      return;
    }

    if (cartSubtotal <= 0) {
      toast.error("Invalid cart subtotal");
      return;
    }

    console.log("Validating coupon with cart items:", cartItems);
    console.log("Cart subtotal:", cartSubtotal);

    setIsValidating(true);

    try {
      const response = await axios.post("/coupons/validate", {
        code: couponCode.toUpperCase(),
        cartItems: cartItems,
        cartTotal: cartSubtotal, // Use subtotal only for discount calculation
      });

      if (response.data.success) {
        toast.success("Coupon is valid!");
        return response.data.data;
      } else {
        toast.error(response.data.message || "Invalid coupon code");
        return null;
      }
    } catch (error) {
      console.error("Error validating coupon:", error);
      const errorMessage =
        error.response?.data?.message || "Failed to validate coupon";
      toast.error(errorMessage);
      return null;
    } finally {
      setIsValidating(false);
    }
  };

  // Apply coupon
  const applyCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error("Please enter a coupon code");
      return;
    }

    if (appliedDiscount) {
      toast.info(
        "A discount is already applied. Remove it first to apply a new one."
      );
      return;
    }

    console.log("Applying coupon with cart items:", cartItems);
    console.log("Cart subtotal:", cartSubtotal);

    setIsApplying(true);

    try {
      const response = await axios.post("/coupons/apply", {
        code: couponCode.toUpperCase(),
        cartItems: cartItems,
        cartTotal: cartSubtotal, // Use subtotal only for discount calculation
      });

      if (response.data.success) {
        const discountData = response.data.data;
        toast.success(
          `Coupon applied! You saved LKR ${discountData.discountAmount.toFixed(2)}`
        );

        // Call parent callback with discount info
        if (onDiscountApplied) {
          onDiscountApplied({
            code: discountData.discountCode,
            amount: discountData.discountAmount,
            originalTotal: discountData.originalTotal,
            finalTotal: discountData.finalTotal,
            discountId: discountData.discountId,
            applicableItemsTotal: discountData.applicableItemsTotal,
          });
        }

        setCouponCode("");
      } else {
        toast.error(response.data.message || "Failed to apply coupon");
      }
    } catch (error) {
      console.error("Error applying coupon:", error);
      const errorMessage =
        error.response?.data?.message || "Failed to apply coupon";
      toast.error(errorMessage);
    } finally {
      setIsApplying(false);
    }
  };

  // Remove applied discount
  const removeDiscount = () => {
    if (onDiscountRemoved) {
      onDiscountRemoved();
    }
    toast.info("Discount removed");
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    applyCoupon();
  };

  return (
    <div className={`discount-coupon-component ${className}`}>
      {/* Applied Discount Display */}
      {appliedDiscount && (
        <div className="applied-discount mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex justify-between items-center">
            <div>
              <h4 className="text-sm font-medium text-green-800 mb-1">
                Coupon Applied: {appliedDiscount.code}
              </h4>
              <p className="text-sm text-green-600">
                You saved LKR {appliedDiscount.amount.toFixed(2)}
              </p>
            </div>
            <button
              type="button"
              onClick={removeDiscount}
              className="text-red-500 hover:text-red-700 text-sm font-medium"
              disabled={disabled}
            >
              Remove
            </button>
          </div>
        </div>
      )}

      {/* Coupon Input Section */}
      {!appliedDiscount && (
        <div className="coupon-input-section">
          <h4 className="text-sm font-medium text-qblack mb-3">
            Have a coupon code?
          </h4>
          <p className="text-xs text-gray-500 mb-2">
            Discount applies to product subtotal only (shipping excluded)
          </p>
          <form onSubmit={handleSubmit} className="flex gap-2">
            <div className="flex-1">
              <input
                type="text"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                placeholder="Enter coupon code"
                className="w-full h-[40px] px-3 border border-gray-300 rounded text-sm focus:border-pink-500 focus:ring focus:ring-pink-100 transition"
                disabled={disabled || isValidating || isApplying}
                maxLength={20}
              />
            </div>
            <button
              type="submit"
              disabled={
                disabled || isValidating || isApplying || !couponCode.trim()
              }
              className={`px-4 py-2 rounded text-sm font-medium transition-all duration-150 ${
                disabled || isValidating || isApplying || !couponCode.trim()
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "bg-qblack text-white hover:bg-gray-800"
              }`}
            >
              {isApplying
                ? "Applying..."
                : isValidating
                  ? "Checking..."
                  : "Apply"}
            </button>
          </form>

          {/* Validation Button (Optional - for separate validation) */}
          <div className="mt-2">
            <button
              type="button"
              onClick={validateCoupon}
              disabled={
                disabled || isValidating || isApplying || !couponCode.trim()
              }
              className="text-sm text-pink-600 hover:text-pink-800 underline disabled:text-gray-400 disabled:no-underline"
            >
              {isValidating ? "Validating..." : "Check coupon validity"}
            </button>
          </div>
        </div>
      )}

      {/* Discount Information */}
      {appliedDiscount && (
        <div className="discount-info mt-3 p-3 bg-gray-50 rounded">
          <div className="text-xs text-gray-600">
            <div className="flex justify-between">
              <span>Original Total:</span>
              <span>LKR {appliedDiscount.originalTotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-green-600 font-medium">
              <span>Discount:</span>
              <span>-LKR {appliedDiscount.amount.toFixed(2)}</span>
            </div>
            <div className="flex justify-between font-medium border-t pt-1 mt-1">
              <span>Final Total:</span>
              <span>LKR {appliedDiscount.finalTotal.toFixed(2)}</span>
            </div>
          </div>
        </div>
      )}

      {/* Coupon Guidelines */}
      <div className="coupon-guidelines mt-4 text-xs text-gray-500">
        <p>• Discount applies to product subtotal only (excluding shipping)</p>
        <p>• Coupon codes are case-insensitive</p>
        <p>• Only one coupon can be applied per order</p>
        <p>• Some coupons may have minimum order requirements</p>
        <p>• Expired or invalid coupons cannot be applied</p>
      </div>
    </div>
  );
}
